import { STORY_SOURCE, story } from '@/content/storyBuilder'

// The first written page. Everything below the header comes from the full-page
// capture of digitopia.co/customers/petrol-ofisi-group/ sent on 2026-08-27;
// the closing quote is not repeated here because `content/testimonials.ts`
// already holds it, keyed to this slug, and two copies of a customer's words
// is one copy that can go stale.
//
// **Its four photographs arrived on 2026-08-28** — "birebir tıpa tıp aynısını
// tasarla", with a fresh five-part capture of the same page. That closed the
// gap the previous check named: the montage under the standfirst and the
// forecourt after the challenge are the two bands the reference runs, and the
// two panel pictures fill the empty halves of "The Approach" and "The Results"
// that the capture shows as blank grey (the reference's own images did not
// render in it). Sides are the capture's: the Approach panel sets its words
// right, the Results panel sets them left.
export const added = story('petrol-ofisi-group', 'Petrol Ofisi Group', {
  doc: '1a8b9eed-e11d-4771-bc35-0373ec110c6a-Petrol Ofisi and Digitopia.pdf',
  video: 'shjO8FHtfEU',
  lede: [
    'To accelerate its transformation, Petrol Ofisi partnered with Digitopia to leverage the '
    + 'Digital Maturity Index (DMI) solution.',
  ],
  intro:
    'Petrol Ofisi Group is one of Turkey’s leading energy infrastructure companies, supplying '
    + 'fuel and lubricants across multiple sectors, including retail, aviation, marine, and '
    + 'logistics. With over 80 years of expertise, a vast network of 1,930+ stations, and a '
    + 'strong focus on innovation, the company continues to shape the future of the energy '
    + 'industry by integrating cutting-edge technology into its operations.',
  photo: {
    src: '/photo/petrol-ofisi-operations-montage.webp',
    alt: 'A montage of Petrol Ofisi’s operations: storage spheres at a terminal, a lubricants '
      + 'laboratory and blending plant, container ships and yachts from the air, a PO/Air '
      + 'sustainable aviation fuel tanker beside a departing aircraft, and a lit station '
      + 'forecourt, all cut through by the brand’s red mark.',
  },
  sections: [
    {
      heading: 'The Need: Modernizing for the Future',
      body: [
        'As a regional energy leader, Petrol Ofisi recognized the necessity of future-proofing '
        + 'its operations. The company sought to enhance efficiency, integrate digital '
        + 'capabilities, and optimize decision-making processes. However, achieving this '
        + 'required more than just technology upgrades; it necessitated a comprehensive '
        + 'digital transformation strategy to drive sustainable success.',
      ],
    },
    {
      heading: 'The Challenge: A Shift Beyond IT',
      body: [
        'Petrol Ofisi had already embarked on a significant transformation, migrating its ERP '
        + 'system to SAP. However, digital transformation is not just about system upgrades. '
        + 'It required:',
      ],
      items: [
        { text: 'A cultural shift to embrace digital across the organization.' },
        { text: 'Cross-functional alignment to ensure company-wide adoption.' },
        {
          text: 'A structured approach to measuring progress and making data-driven decisions.',
        },
      ],
      after: [
        'The company needed a strategic framework to align its digital goals with global best '
        + 'practices and engage stakeholders at every level.',
      ],
      photo: {
        src: '/photo/petrol-ofisi-station-forecourt.webp',
        alt: 'A Petrol Ofisi service station seen from the street on a clear day, its red '
          + 'canopy carrying the brand mark, with PO/Gaz pumps and cars refuelling beneath it.',
      },
    },
    {
      heading: 'Why Digitopia: A Strategic Partner for Holistic Transformation',
      band: true,
      body: [
        'To accelerate its transformation, Petrol Ofisi partnered with Digitopia to leverage '
        + 'the Digital Maturity Index (DMI) solution. Unlike traditional IT-focused '
        + 'assessments, Digitopia’s approach evaluated six critical dimensions: technology, '
        + 'governance, people, innovation, processes, and customer experience. The '
        + 'collaboration provided Petrol Ofisi with:',
      ],
      items: [
        { text: 'A clear benchmark of its digital maturity.' },
        { text: 'Actionable insights tailored to its business needs.' },
        { text: 'A customized digital roadmap for strategic execution.' },
      ],
    },
    {
      heading: 'The Approach: A Holistic Digital Roadmap',
      band: true,
      photo: {
        src: '/photo/petrol-ofisi-attendant-at-pump.webp',
        alt: 'A Petrol Ofisi attendant at the pump island under the station canopy, operating '
          + 'the dispenser beside a waiting car.',
      },
      photoSide: 'left',
      body: [
        'With Digitopia’s support, Petrol Ofisi developed a structured, action-oriented '
        + 'transformation strategy focusing on three key pillars:',
      ],
      items: [
        {
          lead: 'Building a Robust IT Infrastructure',
          text: 'Enhancing reliability and scalability to support long-term growth.',
        },
        {
          lead: 'Leveraging Smart Technologies',
          text: 'Implementing AI-driven personalization, automation, and predictive analytics '
            + 'to improve customer experience.',
        },
        {
          lead: 'Investing in Talent Development',
          text: 'Upskilling teams to drive digital transformation from within.',
        },
      ],
      after: [
        'Digitopia’s DMI solution offered a structured approach to tackle these challenges '
        + 'head-on. By evaluating Petrol Ofisi’s digital capabilities across six key '
        + 'dimensions, Digitopia provided a holistic view of the company’s digital maturity, '
        + 'enabling the CTO to make informed decisions and set a strategic roadmap for digital '
        + 'excellence.',
      ],
    },
    {
      heading: 'The Results: A More Resilient & Digital-First Organization',
      band: true,
      photo: {
        src: '/photo/petrol-ofisi-station-at-night.webp',
        alt: 'A Petrol Ofisi station lit up in a mountain valley at night, the brand’s mark '
          + 'drawn above it in a single ribbon of light following the road.',
      },
      photoSide: 'right',
      body: ['Through its collaboration with Digitopia, Petrol Ofisi has achieved:'],
      items: [
        {
          lead: 'Team Alignment',
          text: 'Ensuring all business units contribute to digital initiatives.',
        },
        {
          lead: 'Enhanced Digital Maturity',
          text: 'The company has made substantial progress in its digital capabilities, '
            + 'positioning itself as a leader in the competitive e-commerce space.',
        },
        {
          lead: 'Strategic Clarity and Direction',
          text: 'The DMI scores and analysis provided a clear roadmap for digital '
            + 'transformation, aligning the entire organization around shared goals and '
            + 'objectives.',
        },
      ],
    },
  ],
  check:
    'This page’s words were transcribed from a full-page screenshot of '
    + `${STORY_SOURCE}/petrol-ofisi-group/ supplied on 2026-08-27, not from a file we hold, so `
    + 'two things need checking against the source before this goes to production: the figures '
    + 'in the opening paragraph (“over 80 years”, “1,930+ stations”), and the section headings, '
    + 'which are the live page’s and may since have been rewritten. The photography gap is '
    + 'closed — four Petrol Ofisi pictures arrived on 2026-08-28 with a second, five-part '
    + 'capture of the same page — but where two of them sit is a judgement rather than a '
    + 'reading, and worth a glance. The montage under the standfirst and the forecourt after '
    + '“The Challenge” are where the capture plainly shows pictures; the attendant at the pump '
    + 'and the station at night fill the empty halves of “The Approach” and “The Results”, '
    + 'which the capture shows as blank grey because the reference’s own images did not load '
    + 'in it, so the sides are certain and which picture goes in which is not. The two '
    + 'originals are also small — 900×462 and 768×446, which is what the live site serves — so '
    + 'they are drawn at that width and will look soft on a high-density screen until larger '
    + 'files arrive.',
})

/** Its place in the hand-placed order the imported Customers page set. */
export const order = 6
